import React, {useState, useRef, useEffect} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {ReactComponent as PlayIcon} from '../../../../common/assets/play-circle-icon.svg'
import {ReactComponent as PauseIcon} from '../../../../common/assets/pause-circle-icon.svg'
import i18n from '../../../../common/i18n'

interface XpertKnowledgeEditSubclipModalProps {
  fullrecordingUrl?: string
  startTimestamp?: string
  endTimestramp?: string
  onStartTimestampChange: (timestamp: string) => void
  onEndTimestampChange: (timestamp: string) => void
}

const XpertKnowledgeEditSubclipModal: React.FC<XpertKnowledgeEditSubclipModalProps> = ({
  fullrecordingUrl,
  startTimestamp,
  endTimestramp,
  onStartTimestampChange,
  onEndTimestampChange,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [startTime, setStartTime] = useState(0)
  const [endTime, setEndTime] = useState(0)
  const [isDraggingStart, setIsDraggingStart] = useState(false)
  const [isDraggingEnd, setIsDraggingEnd] = useState(false)
  const [isDraggingCurrent, setIsDraggingCurrent] = useState(false)

  const timestampToSeconds = (timestamp: string): number => {
    if (!timestamp) return 0
    const parts = timestamp.split(':').map(Number)
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1]
    }
    return 0
  }

  useEffect(() => {
    if (startTimestamp && videoRef.current) {
      const startSeconds = timestampToSeconds(startTimestamp)
      setCurrentTime(startSeconds)
      videoRef.current.currentTime = startSeconds
    }
  }, [startTimestamp])

  const secondsToTimestamp = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    if (startTimestamp) {
      setStartTime(timestampToSeconds(startTimestamp))
    }
    if (endTimestramp) {
      setEndTime(timestampToSeconds(endTimestramp))
    }
  }, [startTimestamp, endTimestramp])

  useEffect(() => {
    if (duration > 0 && !endTimestramp) {
      setEndTime(duration)
    }
  }, [duration, endTimestramp])

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDraggingCurrent) {
      const time = videoRef.current.currentTime
      setCurrentTime(time)

      if (time >= endTime && isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        if (currentTime < startTime || currentTime >= endTime) {
          videoRef.current.currentTime = startTime
          setCurrentTime(startTime)
        }
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const seekTo = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, Math.min(time, duration))
      setCurrentTime(time)
    }
  }

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const newTime = percentage * duration
    seekTo(newTime)
  }

  const handleMouseDown = (type: 'start' | 'end' | 'current') => (e: React.MouseEvent) => {
    e.preventDefault()
    if (type === 'start') setIsDraggingStart(true)
    else if (type === 'end') setIsDraggingEnd(true)
    else setIsDraggingCurrent(true)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingStart && !isDraggingEnd && !isDraggingCurrent) return

    const timeline = document.querySelector('.timeline-container') as HTMLElement
    if (!timeline) return

    const rect = timeline.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const percentage = Math.max(0, Math.min(1, mouseX / rect.width))
    const newTime = percentage * duration

    if (isDraggingStart) {
      const newStartTime = Math.max(0, Math.min(newTime, endTime - 1))
      setStartTime(newStartTime)
      onStartTimestampChange(secondsToTimestamp(newStartTime))
    } else if (isDraggingEnd) {
      const newEndTime = Math.max(startTime + 1, Math.min(newTime, duration))
      setEndTime(newEndTime)
      onEndTimestampChange(secondsToTimestamp(newEndTime))
    } else if (isDraggingCurrent) {
      seekTo(newTime)
    }
  }

  const handleMouseUp = () => {
    setIsDraggingStart(false)
    setIsDraggingEnd(false)
    setIsDraggingCurrent(false)
  }

  useEffect(() => {
    if (isDraggingStart || isDraggingEnd || isDraggingCurrent) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDraggingStart, isDraggingEnd, isDraggingCurrent, duration, startTime, endTime])

  const jumpToStart = () => seekTo(startTime)
  const jumpToEnd = () => seekTo(endTime)

  if (!fullrecordingUrl) {
    return (
      <Block padding="20px" textAlign="center" color="#666">
        {i18n.t('xk.tutorial.edit.subSections.noVideoUrl')}
      </Block>
    )
  }

  const startPercentage = duration > 0 ? (startTime / duration) * 100 : 0
  const endPercentage = duration > 0 ? (endTime / duration) * 100 : 100
  const currentPercentage = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <Col gap="20px" width="600px" maxWidth="90vw" justifyContent="center">
      <Block position="relative" backgroundColor="#000" borderRadius="8px" overflow="hidden">
        <video
          ref={videoRef}
          src={fullrecordingUrl}
          style={{width: '100%', height: 'auto', display: 'block'}}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      </Block>

      <Row alignItems="center">
        <Block
          component="button"
          padding="8px"
          backgroundColor="#007AFF"
          borderRadius="50%"
          border="none"
          cursor="pointer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          hoverBackgroundColor="#0056CC"
          props={{onClick: togglePlay}}
        >
          {isPlaying ? <PauseIcon width={16} height={16} color="#FFF" /> : <PlayIcon width={16} height={16} color="#FFF" />}
        </Block>

        <Block marginLeft="15px" fontSize="14px" fontWeight={500} minWidth="42px">
          {secondsToTimestamp(currentTime)}
        </Block>

        <Block fontSize="14px" fontWeight={500} color="#666">
          / {secondsToTimestamp(duration)}
        </Block>
      </Row>

      <Col gap="10px">
        <Block fontSize="14px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.subSections.trimVideo')}
        </Block>

        <Block
          className="timeline-container"
          position="relative"
          height="40px"
          backgroundColor="#E5E7EB"
          borderRadius="4px"
          cursor="pointer"
          props={{onClick: handleTimelineClick}}
        >
          <Block
            position="absolute"
            top="0"
            height="100%"
            backgroundColor="#007AFF"
            opacity={0.3}
            left={`${startPercentage}%`}
            width={`${endPercentage - startPercentage}%`}
          />

          <Block
            position="absolute"
            top="50%"
            left={`${startPercentage}%`}
            width="12px"
            height="20px"
            backgroundColor="#007AFF"
            borderRadius="2px"
            cursor="ew-resize"
            transform="translate(-50%, -50%)"
            props={{onMouseDown: handleMouseDown('start')}}
          />

          <Block
            position="absolute"
            top="50%"
            left={`${endPercentage}%`}
            width="12px"
            height="20px"
            backgroundColor="#007AFF"
            borderRadius="2px"
            cursor="ew-resize"
            transform="translate(-50%, -50%)"
            props={{onMouseDown: handleMouseDown('end')}}
          />

          <Block
            position="absolute"
            top="0"
            left={`${currentPercentage}%`}
            width="2px"
            height="100%"
            backgroundColor="#FF3B30"
            transform="translateX(-50%)"
            props={{onMouseDown: handleMouseDown('current')}}
            cursor="ew-resize"
          />
        </Block>
      </Col>

      <Row gap="20px" alignItems="center" justifyContent="space-between">
        <Col gap="5px" flex="1">
          <Block fontSize="12px" fontWeight={500} color="#666">
            {i18n.t('xk.tutorial.edit.subSections.startTime')}
          </Block>
          <Row alignItems="center" gap="10px">
            <Block
              fontSize="14px"
              fontWeight={500}
              padding="8px 12px"
              backgroundColor="#F3F4F6"
              borderRadius="4px"
              minWidth="80px"
              textAlign="center"
            >
              {secondsToTimestamp(startTime)}
            </Block>
            <Block
              component="button"
              padding="6px 12px"
              fontSize="12px"
              backgroundColor="#E5E7EB"
              border="1px solid #D1D5DB"
              borderRadius="4px"
              cursor="pointer"
              hoverBackgroundColor="#D1D5DB"
              props={{onClick: jumpToStart}}
            >
              {i18n.t('xk.tutorial.edit.subSections.jumpTo')}
            </Block>
          </Row>
        </Col>

        <Col gap="5px" flex="1">
          <Block fontSize="12px" fontWeight={500} color="#666">
            {i18n.t('xk.tutorial.edit.subSections.endTime')}
          </Block>
          <Row alignItems="center" gap="10px">
            <Block
              fontSize="14px"
              fontWeight={500}
              padding="8px 12px"
              backgroundColor="#F3F4F6"
              borderRadius="4px"
              minWidth="80px"
              textAlign="center"
            >
              {secondsToTimestamp(endTime)}
            </Block>
            <Block
              component="button"
              padding="6px 12px"
              fontSize="12px"
              backgroundColor="#E5E7EB"
              border="1px solid #D1D5DB"
              borderRadius="4px"
              cursor="pointer"
              hoverBackgroundColor="#D1D5DB"
              props={{onClick: jumpToEnd}}
            >
              {i18n.t('xk.tutorial.edit.subSections.jumpTo')}
            </Block>
          </Row>
        </Col>
      </Row>

      <Row justifyContent="center" padding="10px" backgroundColor="#F9FAFB" borderRadius="4px">
        <Block fontSize="12px" color="#666">
          {i18n.t('xk.tutorial.edit.subSections.clipDuration')}: {secondsToTimestamp(endTime - startTime)}
        </Block>
      </Row>
    </Col>
  )
}

export default XpertKnowledgeEditSubclipModal
